/**
 * Supabase Client Initialization
 *
 * Creates and exposes the Supabase client as both:
 * - window.SupabaseAPI (used by login-auth.js, forgot-password.html, profile.html)
 * - window.supabase.client (used by reset-password.html, index.html)
 *
 * Config is fetched from /api/config (served by Express from Heroku env vars).
 *
 * Exposes window.SupabaseReady — a Promise that resolves when SupabaseAPI is
 * available. Auth code should await this before calling SupabaseAPI methods.
 */

(function () {
  'use strict';

  window.BurgerPrimeConfig = window.BurgerPrimeConfig || {};

  var _resolve;
  window.SupabaseReady = new Promise(function (resolve) { _resolve = resolve; });

  function initClient(url, key) {
    if (!url || !key) {
      return null;
    }

    var createClient;
    if (window.supabase && typeof window.supabase.createClient === 'function') {
      createClient = window.supabase.createClient;
    }

    if (!createClient) {
      return null;
    }

    var client = createClient(url, key, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: typeof localStorage !== 'undefined' ? localStorage : undefined,
        storageKey: 'burgerprime-auth'
      }
    });

    // Expose as window.supabase.client
    window.supabase = window.supabase || {};
    window.supabase.client = client;

    // Create the SupabaseAPI wrapper with consistent interface
    window.SupabaseAPI = {
      auth: {
        signIn: function (opts) {
          return client.auth.signInWithPassword({
            email: opts.email,
            password: opts.password
          }).then(function (res) {
            if (res.error) return { success: false, error: res.error.message };
            return { success: true, user: res.data.user, session: res.data.session };
          });
        },

        signUp: function (opts) {
          return client.auth.signUp({
            email: opts.email,
            password: opts.password,
            options: {
              data: {
                full_name: (opts.firstName || '') + ' ' + (opts.lastName || ''),
                phone: opts.phone || ''
              }
            }
          }).then(function (res) {
            if (res.error) return { success: false, error: res.error.message };
            return { success: true, user: res.data.user, session: res.data.session };
          });
        },

        signInWithOAuth: function (opts) {
          return client.auth.signInWithOAuth({
            provider: opts.provider,
            options: {
              redirectTo: opts.redirectTo,
              queryParams: { access_type: 'offline', prompt: 'consent' }
            }
          }).then(function (res) {
            if (res.error) return { success: false, error: res.error.message };
            return { success: true, data: res.data };
          });
        },

        resetPasswordForEmail: function (email) {
          return client.auth.resetPasswordForEmail(email, {
            redirectTo: window.location.origin + '/reset-password.html'
          }).then(function (res) {
            if (res.error) return { success: false, error: res.error.message };
            return { success: true, data: res.data };
          });
        },

        resetPassword: function (email) {
          return this.resetPasswordForEmail(email);
        },

        getSession: function () {
          return client.auth.getSession().then(function (res) {
            if (res.error) return null;
            return res.data.session;
          });
        },

        signOut: function () {
          return client.auth.signOut().then(function (res) {
            return { success: !res.error, error: res.error ? res.error.message : undefined };
          });
        }
      }
    };

    _resolve(window.SupabaseAPI);
    return client;
  }

  // Fetch config from server and initialize
  fetch('/api/config')
    .then(function (res) { return res.json(); })
    .then(function (config) {
      window.BurgerPrimeConfig = config;
      if (config.supabaseUrl && config.supabaseAnonKey) {
        var result = initClient(config.supabaseUrl, config.supabaseAnonKey);
        if (!result) {
          _resolve(null);
        }
      } else {
        _resolve(null);
      }
    })
    .catch(function () {
      _resolve(null);
    });
})();
